import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../Context/AuthContext';
import {
  getPendingInventories,
  deleteOfflineInventory,
  updateOfflineInventory,
  getPendingCompletionPlots,
  deleteOfflineCompletionPlot,
  updateOfflineCompletionPlot
} from '../utils/offlineDb';

const API_BASE = process.env.REACT_APP_API_URL;

export const useOfflineData = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const { refreshToken } = useAuth();

  const updatePendingCount = useCallback(async () => {
    try {
      const pendingInv = await getPendingInventories();
      const pendingComp = await getPendingCompletionPlots();
      const countInv = pendingInv.filter(item => item.status !== 'synced').length;
      const countComp = pendingComp.filter(item => item.status !== 'synced').length;
      setPendingCount(countInv + countComp);
    } catch (err) {
      console.error('Failed to get pending count:', err);
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
    };
    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial count
    updatePendingCount();

    // Listen to custom updates (e.g. from InventoryForm after saving offline)
    window.addEventListener('offline-inventory-updated', updatePendingCount);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('offline-inventory-updated', updatePendingCount);
    };
  }, [updatePendingCount]);

  const uploadOfflineFiles = async (inventoryId, files, fileType, token) => {
    if (!files || files.length === 0) return;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append('files', file);

      let backendFileType = 'scanner_dataset';
      if (fileType === 'sample_plot_images') backendFileType = 'sample_plot';
      else if (fileType === 'satellite_view_images') backendFileType = 'satellite_view';
      else if (fileType === 'inaccessible_plot_images') backendFileType = 'inaccessible_plot';

      formData.append('file_type', backendFileType);

      const res = await fetch(`${API_BASE}/api/inventory/${inventoryId}/upload_files/`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (!res.ok) {
        throw new Error(`Failed to upload file ${file.name || i} (${fileType})`);
      }
    }
  };

  const syncSingleInventory = async (item, token) => {
    const { form, projectId } = item;
    const isValueEmpty = (value) => value === null || value === undefined || value === '';

    const inventoryPayload = {
      ...form,
      project: projectId,
      recorded_date: form.recorded_date,
      temperature_record_time: form.temperature_record_time || null,
      major_strata: Number(form.major_strata),
      plot_no: Number(form.plot_no),
      sub_plot_no: Number(form.sub_plot_no),
      ward: form.ward ? Number(form.ward) : null,
      crown_cover: !isValueEmpty(form.crown_cover) ? Number(form.crown_cover) : null,
      latitude: !isValueEmpty(form.latitude) ? Number(form.latitude) : null,
      longitude: !isValueEmpty(form.longitude) ? Number(form.longitude) : null,
      altitude: !isValueEmpty(form.altitude) ? Number(form.altitude) : null,
      slope_degree: !isValueEmpty(form.slope_degree) ? Number(form.slope_degree) : null,
      air_temperature: !isValueEmpty(form.air_temperature) ? Number(form.air_temperature) : null,
      ph_soil_color: !isValueEmpty(form.ph_soil_color) ? Number(form.ph_soil_color) : null,
      soil_color: form.soil_color || null,
      place_name: form.place_name || '',
      nearest_village_name: form.nearest_village_name || '',
      fug_name: form.fug_name || '',
      recorded_by: form.recorded_by || '',
      verified_by: form.verified_by || '',
      MAPsHarvest: form.MAPsHarvest || false,
      record_approved: form.record_approved || false,
      record_recheck: form.record_recheck || false,
      inaccessable_plot: form.inaccessible_plot
    };

    // Remove client-only/nested fields that are sent separately
    delete inventoryPayload.sample_plot_images;
    delete inventoryPayload.satellite_view_images;
    delete inventoryPayload.scanner_dataset_pdfs;
    delete inventoryPayload.inaccessible_plot_images;
    delete inventoryPayload.dominant_species;
    delete inventoryPayload.invasive_species;
    delete inventoryPayload.assertive_species;
    delete inventoryPayload.harvest_species;
    delete inventoryPayload.observations;
    delete inventoryPayload.species;
    delete inventoryPayload.inventory_objects;
    delete inventoryPayload.uploaded_files;

    // 1. Post main inventory
    const res = await fetch(`${API_BASE}/api/inventory/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(inventoryPayload)
    });

    if (!res.ok) {
      throw new Error(`Inventory base creation failed: ${res.statusText}`);
    }

    const createdInventory = await res.json();

    // 2. Post inventory objects
    if (form.inventory_objects && form.inventory_objects.length) {
      await Promise.all(form.inventory_objects.map(obj =>
        fetch(`${API_BASE}/api/inventory-objects/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            inventory: createdInventory.id,
            object: obj.object,
            measuring_unit: obj.measuring_unit,
            measurement: obj.measurement ? Number(obj.measurement) : null,
            measuring_unit_another: obj.measuring_unit_another,
            measurement_another: obj.measurement_another ? Number(obj.measurement_another) : null,
            bearing_degree: obj.bearing_degree ? Number(obj.bearing_degree) : null,
            direction: obj.direction,
            distance_from_plot: obj.distance_from_plot ? Number(obj.distance_from_plot) : null
          })
        }).then(r => { if (!r.ok) throw new Error('Failed to submit inventory object'); return r.json(); })
      ));
    }

    // 3. Upload files
    await uploadOfflineFiles(createdInventory.id, form.sample_plot_images, 'sample_plot_images', token);
    await uploadOfflineFiles(createdInventory.id, form.satellite_view_images, 'satellite_view_images', token);
    await uploadOfflineFiles(createdInventory.id, form.scanner_dataset_pdfs, 'scanner_dataset_pdfs', token);
    await uploadOfflineFiles(createdInventory.id, form.inaccessible_plot_images, 'inaccessible_plot_images', token);

    // 4. Dominant species
    if (form.dominant_species && form.dominant_species.length) {
      await Promise.all(form.dominant_species.map(async (item) => {
        const r = await fetch(`${API_BASE}/api/dominent_species/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ dominent_species: item.name, inventory: createdInventory.id })
        });
        if (!r.ok) throw new Error('Failed to submit dominant species');
        const created = await r.json();
        if (item.types && item.types.length) {
          await Promise.all(item.types.map(typeId =>
            fetch(`${API_BASE}/api/speciestype_dominent_species/`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({ species_type: typeId, dominant_species: created.id })
            }).then(tr => { if (!tr.ok) throw new Error('Failed to link dominant species type'); return tr.json(); })
          ));
        }
      }));
    }

    // 5. Invasive species
    if (form.invasive_species && form.invasive_species.length) {
      await Promise.all(form.invasive_species.map(async (item) => {
        const r = await fetch(`${API_BASE}/api/invasive_species/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ invasive_species: item.name, inventory: createdInventory.id })
        });
        if (!r.ok) throw new Error('Failed to submit invasive species');
        const created = await r.json();
        if (item.types && item.types.length) {
          await Promise.all(item.types.map(typeId =>
            fetch(`${API_BASE}/api/speciestype_invasive_species/`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({ species_type: typeId, invasive_species: created.id })
            }).then(tr => { if (!tr.ok) throw new Error('Failed to link invasive species type'); return tr.json(); })
          ));
        }
      }));
    }

    // 6. Assertive species
    if (form.assertive_species && form.assertive_species.length) {
      await Promise.all(form.assertive_species.map(async (item) => {
        const r = await fetch(`${API_BASE}/api/assertive_species/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ assertive_species: item.name, inventory: createdInventory.id })
        });
        if (!r.ok) throw new Error('Failed to submit assertive species');
        const created = await r.json();
        if (item.types && item.types.length) {
          await Promise.all(item.types.map(typeId =>
            fetch(`${API_BASE}/api/speciestype_assertive_species/`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({ species_type: typeId, assertive_species: created.id })
            }).then(tr => { if (!tr.ok) throw new Error('Failed to link assertive species type'); return tr.json(); })
          ));
        }
      }));
    }

    // 7. Harvest species
    if (form.harvest_species && form.harvest_species.length > 0) {
      await Promise.all(form.harvest_species.map(harvestSpecies =>
        fetch(`${API_BASE}/api/harvest_species/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            harvest_species: harvestSpecies.harvest_species,
            harvest_area: harvestSpecies.harvest_area ? Number(harvestSpecies.harvest_area) : null,
            harvest_intensity: harvestSpecies.harvest_intensity,
            harvested_year: harvestSpecies.harvested_year ? Number(harvestSpecies.harvested_year) : null,
            inventory: createdInventory.id
          })
        }).then(res => { if (!res.ok) throw new Error('Failed to submit harvest species'); return res.json(); })
      ));
    }

    // 8. Observations
    if (form.observations && form.observations.length) {
      await Promise.all(form.observations.map(observationId =>
        fetch(`${API_BASE}/api/inventory-observation/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ inventory: createdInventory.id, destruction: observationId })
        }).then(r => { if (!r.ok) throw new Error('Failed to submit observation'); return r.json(); })
      ));
    }

    // 9. Targeted species
    if (form.species && form.species.length > 0) {
      await Promise.all(form.species.map(speciesData =>
        fetch(`${API_BASE}/api/inventory-species/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            inventory: createdInventory.id,
            species: speciesData.species,
            ground_cover: speciesData.ground_cover ? Number(speciesData.ground_cover) : 0,
            number_of_plants: speciesData.number_of_plants ? Number(speciesData.number_of_plants) : 0,
            number_of_regenerations: speciesData.number_of_regenerations ? Number(speciesData.number_of_regenerations) : 0,
            remarks: speciesData.remarks,
            fresh_plant_wt: speciesData.fresh_plant_wt ? Number(speciesData.fresh_plant_wt) : null,
            length_one: speciesData.length_one ? Number(speciesData.length_one) : null,
            length_two: speciesData.length_two ? Number(speciesData.length_two) : null,
            length_three: speciesData.length_three ? Number(speciesData.length_three) : null,
            diameter_one: speciesData.diameter_one ? Number(speciesData.diameter_one) : null,
            diameter_two: speciesData.diameter_two ? Number(speciesData.diameter_two) : null,
            diameter_three: speciesData.diameter_three ? Number(speciesData.diameter_three) : null
          })
        }).then(res => { if (!res.ok) throw new Error('Failed to submit species'); return res.json(); })
      ));
    }

    // 10. Link to project
    await fetch(`${API_BASE}/api/project-inventory/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ project: projectId, inventory: createdInventory.id })
    }).then(r => { if (!r.ok) throw new Error('Failed to link inventory to project.'); });
  };

  const syncOfflineInventories = useCallback(async () => {
    if (!navigator.onLine || isSyncing) return;

    let token = localStorage.getItem('access_token');
    if (!token) {
      console.warn('Sync aborted: No authentication token found');
      return;
    }

    setIsSyncing(true);
    setSyncError(null);

    try {
      const pending = await getPendingInventories();
      const pendingCompletions = await getPendingCompletionPlots();

      if (pending.length === 0 && pendingCompletions.length === 0) {
        setIsSyncing(false);
        return;
      }

      if (pending.length > 0) {
        console.log(`[Offline Sync] Starting sync for ${pending.length} inventories...`);

        for (const item of pending) {
          if (item.status === 'syncing') continue;

          try {
            await updateOfflineInventory(item.id, { status: 'syncing', error: null });
            updatePendingCount();

            // Attempt to sync
            await syncSingleInventory(item, token);

            // Success - remove from IndexedDB
            await deleteOfflineInventory(item.id);
            console.log(`[Offline Sync] Successfully synced inventory record ${item.id}`);
          } catch (err) {
            // If error is unauthorized (401), attempt token refresh once
            if (err.message.includes('401') || err.message.includes('Unauthorized') || err.message.includes('token')) {
              try {
                console.log('[Offline Sync] Token expired, attempting refresh...');
                const newToken = await refreshToken();
                if (newToken) {
                  token = newToken;
                  // Retry syncing
                  await syncSingleInventory(item, token);
                  await deleteOfflineInventory(item.id);
                  console.log(`[Offline Sync] Successfully synced inventory ${item.id} after token refresh`);
                  continue;
                }
              } catch (refreshErr) {
                console.error('[Offline Sync] Token refresh failed during sync:', refreshErr);
              }
            }

            console.error(`[Offline Sync] Sync failed for inventory ${item.id}:`, err);
            await updateOfflineInventory(item.id, { status: 'failed', error: err.message });
          }
        }
      }

      if (pendingCompletions.length > 0) {
        console.log(`[Offline Sync] Starting sync for ${pendingCompletions.length} completion plots...`);

        for (const compItem of pendingCompletions) {
          if (compItem.status === 'syncing') continue;

          try {
            await updateOfflineCompletionPlot(compItem.id, { status: 'syncing', error: null });
            updatePendingCount();

            const res = await fetch(`${API_BASE}/api/inventory-completion-plot/`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify(compItem.payload)
            });

            if (!res.ok) {
              const errBody = await res.json().catch(() => ({}));
              throw new Error(errBody.detail || `HTTP ${res.status}`);
            }

            await deleteOfflineCompletionPlot(compItem.id);
            console.log(`[Offline Sync] Successfully synced completion plot record ${compItem.id}`);
          } catch (err) {
            console.error(`[Offline Sync] Sync failed for completion plot ${compItem.id}:`, err);
            await updateOfflineCompletionPlot(compItem.id, { status: 'failed', error: err.message });
          }
        }
      }
    } catch (err) {
      console.error('[Offline Sync] Global sync error:', err);
      setSyncError(err.message);
    } finally {
      setIsSyncing(false);
      updatePendingCount();
    }
  }, [isSyncing, updatePendingCount, refreshToken]);

  // Sync automatically when transitioning back online
  useEffect(() => {
    if (isOnline && pendingCount > 0) {
      syncOfflineInventories();
    }
  }, [isOnline, pendingCount, syncOfflineInventories]);

  return {
    isOnline,
    pendingCount,
    isSyncing,
    syncError,
    syncOfflineInventories,
    updatePendingCount
  };
};
